import decoderUrl from '../../../../../node_modules/mpg123-decoder/dist/mpg123-decoder.min.js?url';
import { encodePcmWav } from './encode-pcm-wav';

interface MpegDecoderInstance {
  ready: Promise<void>;
  decode: (data: Uint8Array) => { channelData: Float32Array[]; sampleRate: number };
  free: () => void;
}

interface MpegDecoderCtor {
  new (): MpegDecoderInstance;
}

/**
 * Loads the UMD build as a classic script so Vite does not have to ESM-import
 * the decoder's CJS/WASM graph (that path crashes the app).
 */
function loadMpegDecoderCtor(): Promise<MpegDecoderCtor> {
  const globalDecoders = globalThis as unknown as {
    'mpg123-decoder'?: { MPEGDecoder: MpegDecoderCtor };
  };
  if (globalDecoders['mpg123-decoder']?.MPEGDecoder) {
    return Promise.resolve(globalDecoders['mpg123-decoder'].MPEGDecoder);
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = decoderUrl;
    script.async = true;
    script.onload = () => {
      const ctor = globalDecoders['mpg123-decoder']?.MPEGDecoder;
      if (!ctor) {
        reject(new Error('mpg123-decoder UMD did not expose MPEGDecoder'));
        return;
      }
      resolve(ctor);
    };
    script.onerror = () => reject(new Error('Failed to load mpg123-decoder script'));
    document.head.appendChild(script);
  });
}

/**
 * Decodes an MPEG Layer I/II/III elementary stream to a PCM WAV blob.
 * Used for BWF files that store MP2, which native HTML audio cannot play.
 */
export async function decodeMpegToPcmWav(mpegBytes: Uint8Array): Promise<Blob> {
  const MPEGDecoder = await loadMpegDecoderCtor();
  const decoder = new MPEGDecoder();
  await decoder.ready;
  try {
    const decoded = decoder.decode(mpegBytes);
    if (!decoded.channelData?.length || !decoded.sampleRate) {
      throw new Error('MPEG decoder returned no audio');
    }
    return encodePcmWav(decoded.channelData, decoded.sampleRate);
  } finally {
    decoder.free();
  }
}
